import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { selectCls, inputCls } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { Handshake, Loader2, Trash2, Lock } from "lucide-react";

export const OUT_STATUS = { enviado: "Enviado", em_execucao: "Em execução", retornou: "Retornou" };
const norm = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();

function OutForm({ order, desc, rec, partners, onSaved }) {
  const [f, setF] = useState({ partner_id: rec?.partner_id || "", cost: rec?.cost ?? "", sent_at: rec?.sent_at || "", returned_at: rec?.returned_at || "", status: rec?.status || "enviado", note: rec?.note || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/orders/${order.id}/outsourced`, { ...f, description: desc, cost: Number(f.cost) || 0 }); toast.success("Terceirizado salvo"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const tid = `out-${norm(desc).replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div data-testid={`${tid}-form`} className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
      <select data-testid={`${tid}-partner`} value={f.partner_id} onChange={set("partner_id")} className={`${selectCls} col-span-2 sm:col-span-1`}>
        <option value="">Parceiro…</option>
        {partners.map((p) => <option key={p.id} value={p.id}>{p.trade_name || p.name}</option>)}
      </select>
      <Input data-testid={`${tid}-cost`} type="number" step="0.01" value={f.cost} onChange={set("cost")} placeholder="Custo pago ao terceiro (R$)" className={inputCls} />
      <select data-testid={`${tid}-status`} value={f.status} onChange={set("status")} className={selectCls}>
        {Object.entries(OUT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <label className="text-[11px] text-zinc-400">Envio<input data-testid={`${tid}-sent`} type="date" value={f.sent_at} onChange={set("sent_at")} className={selectCls} /></label>
      <label className="text-[11px] text-zinc-400">Retorno<input data-testid={`${tid}-returned`} type="date" value={f.returned_at} onChange={set("returned_at")} className={selectCls} /></label>
      <Input data-testid={`${tid}-note`} value={f.note} onChange={set("note")} placeholder="Observação interna" className={`${inputCls} col-span-2 sm:col-span-1 self-end`} />
      <div className="col-span-2 sm:col-span-3 flex justify-end">
        <Button data-testid={`${tid}-save`} size="sm" disabled={busy || !f.partner_id} onClick={save} className="bg-green-brand hover:bg-green-brandDark text-white font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
      </div>
    </div>
  );
}

function OutRow({ order, desc, price, rec, partners, onSaved, orphan }) {
  const [open, setOpen] = useState(!!rec && !orphan);
  const remove = async () => {
    if (!window.confirm("Remover terceirizado e a conta a pagar gerada?")) return;
    try { await api.delete(`/outsourced/${rec.id}`); onSaved(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const tid = `out-${norm(desc).replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div data-testid={`${tid}-row`} className="rounded-lg border border-zinc-800 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0"><div className="text-sm text-zinc-100 truncate">{desc}{orphan && <span className="ml-2 text-[11px] text-amber-300">(item saiu do orçamento)</span>}</div>
          {rec && <div className="text-[11px] text-zinc-400">{rec.partner_name} · {OUT_STATUS[rec.status]} · custo {brl(rec.cost)}{price != null && ` · lucro ${brl(price - rec.cost)}`} · conta {rec.payable_status === "pago" ? "paga" : "em aberto"}</div>}
        </div>
        <div className="flex items-center gap-2">
          {!orphan && <label className="flex items-center gap-1.5 text-xs text-zinc-300"><input data-testid={`${tid}-toggle`} type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} disabled={!!rec} /> Terceirizado</label>}
          {rec && <button data-testid={`${tid}-delete`} onClick={remove} className="p-1 text-zinc-400 hover:text-red-300" aria-label="Remover"><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>
      {open && !orphan && <OutForm order={order} desc={desc} rec={rec} partners={partners} onSaved={onSaved} />}
    </div>
  );
}

export function OutsourcedCard({ order }) {
  const [recs, setRecs] = useState([]);
  const [partners, setPartners] = useState([]);
  const load = useCallback(() => api.get(`/orders/${order.id}/outsourced`).then(({ data }) => setRecs(data)).catch(() => {}), [order.id]);
  useEffect(() => { load(); api.get("/erp/suppliers", { params: { kind: "prestador" } }).then(({ data }) => setPartners(data.filter((p) => p.shop_id === order.shop_id))).catch(() => {}); }, [load, order.shop_id]);
  const services = (order.budget?.items || []).filter((i) => !i.product_id && i.description);
  const byDesc = Object.fromEntries(recs.map((r) => [norm(r.description), r]));
  const orphans = recs.filter((r) => !services.some((s) => norm(s.description) === norm(r.description)));
  return (
    <section data-testid="outsourced-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="font-display text-xl font-bold uppercase text-zinc-100 flex items-center gap-2"><Handshake className="h-5 w-5 text-green-brand" /> Serviços terceirizados</h2>
        <Link to="/owner/terceirizados" data-testid="outsourced-report-link" className="text-xs text-green-neon hover:underline">Relatório</Link>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3 flex items-center gap-1"><Lock className="h-3 w-3" /> Só o dono vê. O cliente vê apenas o nome do serviço e o preço.</p>
      {!services.length && !orphans.length ? <p className="text-sm text-zinc-500">Adicione serviços no orçamento para marcar como terceirizado.</p> : (
        <div className="space-y-2">
          {!partners.length && <p data-testid="outsourced-no-partners" className="text-xs text-amber-300">Cadastre um parceiro em <Link to="/owner/fornecedores" className="underline">Fornecedores</Link> com tipo "Prestador de serviço".</p>}
          {services.map((s, i) => <OutRow key={`${i}-${s.description}`} order={order} desc={s.description} price={(Number(s.quantity) || 0) * (Number(s.unit_price) || 0)} rec={byDesc[norm(s.description)]} partners={partners} onSaved={load} />)}
          {orphans.map((r) => <OutRow key={r.id} order={order} desc={r.description} rec={r} partners={partners} onSaved={load} orphan />)}
        </div>
      )}
    </section>
  );
}
